import {
  IAssetCategoriesListModel,
  IResourceModel,
} from "@xala/common-services";
import {
  Button,
  Col,
  Dragger,
  Form,
  Icon,
  IFormValues,
  Input,
  Link,
  PreviewImage,
  Row,
} from "@xala/common-ui";
import React from "react";
import { WithTranslation } from "react-i18next";
import * as TranslationsModule from "../../../../../Translations";
import { ActionProperties } from "../ActionProperties";
import {
  ComponentPropertiesBase,
  IComponentPropertiesBaseProps,
  IComponentPropertiesBaseState,
} from "../ComponentPropertiesBase";
import { ApplicationBannerItemPropertyModel } from "../../models/ApplicationBannerItemPropertyModel";
import { RcFile } from "antd/lib/upload";
import { imageBannerSizeHelper } from "../../../../../Asset/helpers/assetImagesTypeHelper";

export interface IApplicationBannerItemPropertiesStateProps {
  isProcessingData: boolean;
  categories: IAssetCategoriesListModel;
}

export interface IApplicationBannerItemPropertiesDispatchProps {}

export interface IApplicationBannerItemPropertiesOwnProps {}

const PREVIEW_IMAGE_HEIGHT = 60;
const PREVIEW_IMAGE_WIDH = 125;

export interface IApplicationBannerItemPropertiesProps
  extends IApplicationBannerItemPropertiesStateProps,
    IApplicationBannerItemPropertiesDispatchProps,
    IApplicationBannerItemPropertiesOwnProps,
    IComponentPropertiesBaseProps,
    WithTranslation {}

export interface IApplicationBannerItemPropertiesState
  extends IComponentPropertiesBaseState {
  translationsBrowserVisible: boolean;
}

export class ApplicationBannerItemProperties extends ComponentPropertiesBase<
  IApplicationBannerItemPropertiesProps,
  IApplicationBannerItemPropertiesState,
  ApplicationBannerItemPropertyModel
> {
  public static defaultProps = {
    isProcessingData: false,
  };

  public state: Readonly<IApplicationBannerItemPropertiesState> = {
    translationsBrowserVisible: false,
  };

  getPropertiesModel(): ApplicationBannerItemPropertyModel {
    const { component } = this.props;
    const properties = new ApplicationBannerItemPropertyModel();
    properties.init(component);

    return properties;
  }

  public onFinish = (values: IFormValues) => {
    const { component } = this.props;

    if (!component) {
      return;
    }

    component.Name = values.Name;
  };

  public onTitleTranslationKeyCancel = () => {
    this.setState({ translationsBrowserVisible: false });
  };

  public onTitleTranslationKeySelect = (row: IResourceModel) => {
    this.properties.TitleTranslationKey = row.ResourceKey;
    this.onPropertyChange("TitleTranslationKey");

    this.setState({ translationsBrowserVisible: false });
  };

  public onTitleTranslationKeyClear = () => {
    const { component, onComponentChange } = this.props;

    this.properties.TitleTranslationKey = undefined;
    this.properties.deleteProperty("TitleTranslationKey", component);

    if (onComponentChange) {
      onComponentChange(component);
    }
  };

  public renderTitleTranslationKeyProperty = () => {
    const { component, t } = this.props;
    const { translationsBrowserVisible } = this.state;

    let titleTranslationKeyView: React.ReactElement = this.properties
      .TitleTranslationKey ? (
      <label>
        <Link
          to={`${TranslationsModule.ROUTES.CONFIGURATION_TRANSLATION_DETAILS}/${this.properties.TitleTranslationKey}`}
        >
          {this.properties.TitleTranslationKey}
        </Link>
      </label>
    ) : (
      <label>{t("SELECT_TRANSLATION_KEY", "Select translation key")}</label>
    );

    return (
      <Form.Item
        name="TitleTranslationKey"
        label={t("TITLE_TRANSLATION_KEY", "Title Key")}
      >
        <Row gutter={8} style={{ justifyContent: "space-between" }}>
          <Col span={18}>
            <Form.Item>{titleTranslationKeyView}</Form.Item>
            <TranslationsModule.Components.TranslationsBrowserModal
              key={`TranslationsBrowser-${component.Id}`}
              visible={translationsBrowserVisible}
              onCancel={this.onTitleTranslationKeyCancel}
              onSelect={this.onTitleTranslationKeySelect}
            />
          </Col>
          <Col span={6} style={{ display: "flex", flex: "0" }}>
            {this.properties.TitleTranslationKey && (
              <Button
                icon={<Icon type="delete" />}
                onClick={this.onTitleTranslationKeyClear}
                style={{ marginRight: "8px" }}
              />
            )}
            <Button
              icon={<Icon type="edit" />}
              onClick={() => {
                this.setState({ translationsBrowserVisible: true });
              }}
            />
          </Col>
        </Row>
      </Form.Item>
    );
  };

  public renderTitleProperty = () => {
    const { isProcessingData, t } = this.props;

    return (
      <Form.Item
        name="Title"
        label={t("Title")}
        initialValue={this.properties.Title}
      >
        <Input
          disabled={isProcessingData}
          placeholder={t("Enter title")}
          onChange={(e) => {
            this.properties.Title = e.target.value;
            this.onPropertyChange("Title");
          }}
        />
      </Form.Item>
    );
  };

  public renderBackgroundImageUrlProperty = () => {
    const { isProcessingData, t } = this.props;

    return (
      <Form.Item
        name="ImageUrl"
        label={t("Image Url")}
        initialValue={this.properties.ImageUrl}
      >
        <Input
          disabled={isProcessingData}
          placeholder={t("Enter image url")}
          onChange={(e) => {
            this.properties.ImageUrl = e.target.value;
            this.onPropertyChange("ImageUrl");
          }}
        />
      </Form.Item>
    );
  };

  public renderBackgroundImageUploadProperty = () => {
    const { t } = this.props;

    return (
      <Form.Item
        key="FileValue"
        name="FileValue"
        label={t("Upload", "Upload")}
        valuePropName="FileValue"
        getValueFromEvent={this.onImageChange}
      >
        <Dragger
          name="FileValue"
          accept="image/*"
          multiple={false}
          beforeUpload={this.beforeUpload}
          fileList={[]}
          withPreview={true}
          maxCount={1}
        >
          {(previewSrc: string) => {
            return (
              <>
                <p className="ant-upload-drag-icon">
                  <PreviewImage
                    src={previewSrc || this.properties.ImageUrl}
                    height={PREVIEW_IMAGE_HEIGHT}
                    width={PREVIEW_IMAGE_WIDH}
                    placeholderIconType={"inbox"}
                  />
                </p>
                <p className="ant-upload-text">
                  {t(
                    "DRAG_AND_DROP",
                    "Click or drag a file to this area to upload."
                  )}
                </p>
              </>
            );
          }}
        </Dragger>
      </Form.Item>
    );
  };

  public renderActionProperty = () => {
    const { component, onComponentChange } = this.props;

    return (
      <ActionProperties
        key={`Action-${component.Id}`}
        component={component}
        onComponentChange={onComponentChange}
      />
    );
  };

  public renderRecomendedResolution = () => {
    const { t } = this.props;

    return (
      <Form.Item name="Ratio" label={t("Ratio", "Ratio")} key="Ratio">
        {t("SUGGESTED_RATIO_FOR_IMAGE", {
          width: imageBannerSizeHelper.suggestedResolution.width,
          height: imageBannerSizeHelper.suggestedResolution.height,
        })}
      </Form.Item>
    );
  };

  public render() {
    const formItemLayout = {
      labelCol: {
        xs: { span: 24 },
        sm: { span: 8 },
        md: { span: 8 },
        lg: { span: 6 },
      },
      wrapperCol: {
        xs: { span: 24 },
        sm: { span: 16 },
        md: { span: 16 },
        lg: { span: 18 },
      },
    };

    return (
      <Form
        name="ApplicationBannerItemProperties"
        {...formItemLayout}
        onFinish={this.onFinish}
      >
        <Row direction="column" justify="space-between" className="full-height">
          <Col>
            {this.renderTitleProperty()}
            {this.renderTitleTranslationKeyProperty()}
            {this.renderRecomendedResolution()}
            {this.renderBackgroundImageUrlProperty()}
            {this.renderBackgroundImageUploadProperty()}
            {this.renderActionProperty()}
          </Col>
        </Row>
      </Form>
    );
  }
}
